import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHome, FiWifi, FiCoffee, FiShield, FiCamera, FiSun, FiMoon, FiThermometer } = FiIcons;

const Lodge = ({ standalone = false }) => {
  const amenities = [
    {
      icon: FiCamera,
      title: 'Climate-Controlled Media Room',
      description: 'Dehumidified gear storage with 4K monitors and high-speed card readers for photographers.'
    },
    {
      icon: FiCoffee,
      title: 'Early Morning Service',
      description: '4:30 AM breakfast service and 24/7 coffee/tea station for serious birders.'
    },
    {
      icon: FiShield,
      title: 'Secure Equipment Storage',
      description: 'Individual lockers for expensive optical gear with insurance coverage.'
    },
    {
      icon: FiWifi,
      title: 'High-Speed Internet',
      description: 'Upload your daily shots and stay connected with fast, reliable connectivity.'
    },
    {
      icon: FiSun,
      title: 'Photography Hides',
      description: 'Purpose-built hides positioned for optimal lighting and bird activity.'
    },
    {
      icon: FiThermometer,
      title: 'Climate Control',
      description: 'Air-conditioned rooms to protect sensitive equipment from humidity.'
    }
  ];

  const rooms = [
    {
      name: 'Deluxe Forest View',
      image: 'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      features: ['Private balcony overlooking canopy', 'Early morning bird activity viewing', 'Quiet zone for equipment preparation'],
      price: '$180',
      maxGuests: 2
    },
    {
      name: 'Premium Birder Suite',
      image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      features: ['Dedicated gear room with dehumidifier', 'Panoramic forest views', 'Private guide coordination desk'],
      price: '$280',
      maxGuests: 3
    },
    {
      name: 'Photographer\'s Retreat',
      image: 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      features: ['Multiple charging stations', 'Large workspace for photo editing', 'Direct access to photography hides'],
      price: '$350',
      maxGuests: 2
    }
  ];

  return (
    <section className={`${standalone ? 'pt-32' : 'py-20'} relative overflow-hidden`}>
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="liquid-blob"></div>
        <div className="liquid-blob"></div>
      </div>

      <div className="relative z-10 container mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 glass px-6 py-3 rounded-full mb-6">
            <SafeIcon icon={FiHome} className="w-5 h-5 text-green-400" />
            <span className="text-white font-medium">Luxury Eco-Lodge</span>
          </div>
          
          <h2 className="font-display text-4xl md:text-6xl font-bold text-white mb-6">
            Bird-Friendly
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-yellow-400">
              Accommodations
            </span>
          </h2>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Every detail designed for the serious birder and photographer. From climate-controlled 
            gear storage to early morning logistics, we understand your needs.
          </p>
        </motion.div>

        {/* Amenities Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20"
        >
          {amenities.map((amenity, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="glass p-8 rounded-2xl text-center hover:glass-strong transition-all duration-300"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={amenity.icon} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">{amenity.title}</h3>
              <p className="text-gray-300">{amenity.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Room Types */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-16"
        >
          <h3 className="font-display text-3xl md:text-4xl font-bold text-white text-center mb-12">
            Accommodation Options
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {rooms.map((room, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-strong rounded-2xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300"
              >
                <div className="relative">
                  <img
                    src={room.image}
                    alt={room.name}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-4 right-4 glass px-3 py-1 rounded-full">
                    <span className="text-white font-bold">{room.price}/night</span>
                  </div>
                </div>

                <div className="p-6">
                  <h4 className="font-display text-xl font-bold text-white mb-4">
                    {room.name}
                  </h4>
                  
                  <ul className="space-y-2 mb-6">
                    {room.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="text-gray-300 text-sm flex items-start">
                        <span className="w-2 h-2 bg-green-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Max {room.maxGuests} guests</span>
                    <button className="btn-primary px-6 py-2 text-sm">
                      Book Now
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Special Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="glass-strong rounded-2xl p-8 md:p-12"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="font-display text-3xl font-bold text-white mb-6">
                The Birding Concierge Experience
              </h3>
              <p className="text-gray-300 mb-6">
                Our dedicated birding concierge is available 24/7 to coordinate your experience. 
                From pre-arrival species briefings to real-time sighting updates, we ensure you 
                maximize every moment in the field.
              </p>
              
              <div className="space-y-4">
                {[
                  'Real-time species activity updates',
                  'Private guide coordination',
                  'Equipment rental and maintenance',
                  'Photography workshop arrangements',
                  'Conservation tour bookings'
                ].map((service, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-green-400 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">✓</span>
                    </div>
                    <span className="text-gray-300">{service}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:+94717714922"
                  className="btn-primary px-6 py-3 text-center"
                >
                  Call Concierge Now
                </a>
                <a
                  href="https://wa.me/94717714922"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="glass px-6 py-3 text-white font-semibold rounded-xl hover:bg-white/20 transition-all duration-300 text-center"
                >
                  WhatsApp Direct
                </a>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                alt="Lodge Exterior"
                className="w-full h-80 object-cover rounded-xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-xl"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-white font-semibold">
                  15-minute walk to Kudawa entrance
                </p>
                <p className="text-gray-300 text-sm">
                  Prime location for early morning birding
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Lodge;